'use client';

import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@common/components';
import { FOCUS_RING } from '@common/components/interaction';
import { cn } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { AuthQueryOptions, UnauthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { PointCoin } from '@/app/_components/PointCoin';
import { Image } from '@/components/client';
import { ROUTES } from '@/constants';
const FALLBACK_CARD_COUNT = 4;
const GRID_CLASS_NAME = 'grid grid-cols-2 gap-x-12 gap-y-16 px-20 xl:grid-cols-3 xl:gap-x-16 xl:gap-y-24 xl:px-0';
const LOGO_BOX_CLASS_NAME = 'relative flex h-84 items-center justify-center overflow-hidden rounded-large border border-Gray-300 bg-Gray-white xl:h-120 transition-opacity duration-150 group-hover:opacity-80 group-active:opacity-80 motion-reduce:transition-none';

//* 회원·비회원 목록은 같은 응답이다 — 비회원만 allows 경로로 부른다.
export const useRouletteBrands = isGuest => {
  const $ = _c(4);
  let t0;
  if ($[0] !== isGuest) {
    t0 = isGuest ? UnauthQueryOptions["get:/wello/v1/allows/affiliate/partner-points/roulettes"]() : AuthQueryOptions["get:/wello/v1/auth/affiliate/partner-points/roulettes"]();
    $[0] = isGuest;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      ...t0,
      select: _temp
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return useSuspenseQuery(t1);
};
export type RouletteBrand = ReturnType<typeof useRouletteBrands>['data'][number];
interface RouletteBrandCardProps {
  brand: RouletteBrand;
  isWelloApp: boolean;
  onAppOnlyClick: () => void;
}
const RouletteBrandCard = t0 => {
  const $ = _c(28);
  const {
    brand,
    isWelloApp,
    onAppOnlyClick
  } = t0;
  const isBlockedOnWeb = !isWelloApp && brand.appOnlyYN;
  let t1;
  if ($[0] !== brand.rouletteBrandId) {
    t1 = ROUTES.BENEFIT_PARTNER_POINT_ROULETTE.pathname({
      rouletteBrandId: brand.rouletteBrandId
    });
    $[0] = brand.rouletteBrandId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isBlockedOnWeb || $[3] !== onAppOnlyClick) {
    t2 = event => {
      if (isBlockedOnWeb) {
        event.preventDefault();
        onAppOnlyClick();
      }
    };
    $[2] = isBlockedOnWeb;
    $[3] = onAppOnlyClick;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("group flex flex-col gap-8 rounded-large xl:gap-12", FOCUS_RING);
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== brand.imageUrl) {
    t4 = brand.imageUrl ? <Image src={brand.imageUrl} alt="" fill={true} sizes="(min-width: 1280px) 218px, 50vw" className="object-contain p-12" /> : null;
    $[6] = brand.imageUrl;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== isBlockedOnWeb) {
    t5 = isBlockedOnWeb ? <span className="absolute inset-0 flex items-center justify-center bg-Gray-black/40 font-700 text-Gray-white typo-caption1">앱 전용 쿠폰</span> : null;
    $[8] = isBlockedOnWeb;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <span className={LOGO_BOX_CLASS_NAME}>{t4}{t5}</span>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== brand.name) {
    t7 = <span className="truncate font-700 text-Gray-900 typo-body4">{brand.name}</span>;
    $[13] = brand.name;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <PointCoin currency="AFFILIATE_PARTNER_POINT" size={16} />;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== brand.requiredPoint) {
    t9 = brand.requiredPoint.toLocaleString();
    $[16] = brand.requiredPoint;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = <span className="flex items-center gap-2 xl:gap-4">{t8}<span className="font-600 text-B-550 typo-caption1">{t9}P</span></span>;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t7) {
    t11 = <span className="flex flex-col pl-4 xl:gap-4">{t7}{t10}</span>;
    $[20] = t10;
    $[21] = t7;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t1 || $[24] !== t11 || $[25] !== t2 || $[26] !== t6) {
    t12 = <CustomLink prefetch={false} href={t1} onClick={t2} className={t3}>{t6}{t11}</CustomLink>;
    $[23] = t1;
    $[24] = t11;
    $[25] = t2;
    $[26] = t6;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
};
interface RouletteBrandGridProps {
  brands: RouletteBrand[];
  isWelloApp: boolean;
  onAppOnlyClick: () => void;
}
export const RouletteBrandGrid = t0 => {
  const $ = _c(10);
  const {
    brands,
    isWelloApp,
    onAppOnlyClick
  } = t0;
  if (brands.length === 0) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <p className="flex min-h-240 items-center justify-center px-20 font-500 text-Gray-500 typo-body4">지금은 참여할 수 있는 쿠폰이 없어요.</p>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== brands || $[2] !== isWelloApp || $[3] !== onAppOnlyClick) {
    let t2;
    if ($[5] !== isWelloApp || $[6] !== onAppOnlyClick) {
      t2 = brand => <li key={brand.rouletteBrandId} className="flex flex-col"><RouletteBrandCard brand={brand} isWelloApp={isWelloApp} onAppOnlyClick={onAppOnlyClick} /></li>;
      $[5] = isWelloApp;
      $[6] = onAppOnlyClick;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    t1 = brands.map(t2);
    $[1] = brands;
    $[2] = isWelloApp;
    $[3] = onAppOnlyClick;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[8] !== t1) {
    t2 = <ul className={GRID_CLASS_NAME}>{t1}</ul>;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export const RouletteBrandGridFallback = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ul aria-hidden={true} className={GRID_CLASS_NAME}>{Array.from({
        length: FALLBACK_CARD_COUNT
      }, _temp2)}</ul>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
function _temp(response) {
  return response.context?.contents ?? [];
}
function _temp2(_, index) {
  return <li key={index} className="flex flex-col gap-8"><Skeleton className="h-84 w-full rounded-large xl:h-120" /><Skeleton typo="body4" className="w-2/3 rounded-xxsmall" /><Skeleton typo="body4" className="w-1/2 rounded-xxsmall" /></li>;
}